import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { toast } from "@jti/core-components/components/Toaster";
import { newrelic } from "@jti/fe-clients";
import { customPaymentMethods, SinglePageCheckoutRoutes } from "@jti/fe-common";
import { useSelector } from "@jti/store/reduxHooks";
import { useUpdateOrderPaymentMethodMutation } from "@jti/store/slices/magentoApiSlice";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { usePaymentProviderContext } from "@checkout-v2/features/payment/context/PaymentProviderContext";
import { PAYMENT_ADAPTER_MOUNT_NODE_ID } from "@checkout-v2/shared/const";
import { useCustomerOrder } from "@checkout-v2/shared/hooks/useCustomerOrder";
import { usePayForOrder } from "@checkout-v2/shared/hooks/usePayForOrder";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";
import { checkIfSubscriptionOrder } from "@checkout-v2/shared/utils/checkIfSubscriptionOrder";
import { saveOrderCheckoutFlow } from "@checkout-v2/shared/utils/purchaseTrackingStorage";

import type {
  ConsentAndPayFormFieldsConfig,
  ConsentAndPayFormFieldsDefaultValues,
} from "../../ConsentAndPayForm.types";
import { consentFieldFactory } from "../../factories";
import { ConsentAndPayFormRenderer } from "../../renderer/ConsentAndPayFormRenderer";

export const OrderConsentAndPayFormOrchestrator = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { __ } = useTranslations();
  const checkoutConfig = useCheckoutV2Configuration();
  const [updateOrderPaymentMethod] = useUpdateOrderPaymentMethodMutation();
  const { data: orderData } = useCustomerOrder();
  const { adapterStatus, paymentInitiationStatus, isPaymentSessionActive, preloadAdapter, mountAdapter } =
    usePaymentProviderContext();
  const { handlePaymentProcess } = usePayForOrder();

  const isRetryingPayment = location.pathname.includes(SinglePageCheckoutRoutes.paths.orderPaymentFailed);
  const isConsentEnabled = !checkoutConfig.general.policyAndTermsConsentDisabled;

  const isOrderPagePaymentFlowEnabled = checkoutConfig.paymentFlow?.isOrderPagePaymentFlowEnabled;

  const checkoutData = useSelector((state) => state.checkoutV2.data);
  const selectedPaymentMethodCode = checkoutData?.paymentMethodCode;
  const securedFieldsPayment = checkoutData?.securedFieldsPayment;
  const isPayOnDelivery = selectedPaymentMethodCode === customPaymentMethods.payOnDelivery;

  const [isPaymentMethodPreselected] = useState(Boolean(location.state?.paymentMethodPreselected));

  const formFieldsConfig: ConsentAndPayFormFieldsConfig = useMemo(() => {
    return {
      consent: consentFieldFactory.getDerivedConfig({ __, isConsentEnabled }),
    };
  }, [__, isConsentEnabled]);

  const formFieldsDefaultValues: ConsentAndPayFormFieldsDefaultValues = useMemo(() => {
    return {
      orderAttributes: {},
      consent: consentFieldFactory.getDefaultValue({ defaultChecked: isPaymentMethodPreselected }),
    };
  }, [isPaymentMethodPreselected]);

  const redirectToThankYouPage = (orderNumber: string) => {
    const thankYouPagePath = SinglePageCheckoutRoutes.getThankYouPagePath(orderNumber);

    navigate(thankYouPagePath);
  };

  const handlePayOndeliveryOrderSubmit = async () => {
    try {
      if (!orderData) {
        throw new Error("Order data is required to update payment method for Pay on Delivery");
      }

      // Tear down any card session mounted by a previous submit attempt before
      // confirming Pay on Delivery, so it can't complete a charge afterwards.
      unmountAdapterRef.current?.();
      unmountAdapterRef.current = null;

      await updateOrderPaymentMethod({
        orderId: orderData.orderId,
        methodCode: customPaymentMethods.payOnDelivery,
      }).unwrap();

      redirectToThankYouPage(orderData.number);
    } catch (error) {
      const errorMessage = __("checkout.order.consent_and_pay.error.payment_method_update_failed");

      toast.error(errorMessage);
      newrelic.noticeError(error instanceof Error ? error : new Error(errorMessage));
    }
  };

  const performMount = useCallback(async () => {
    if (!orderData || !selectedPaymentMethodCode) {
      return;
    }

    const mountElement = document.getElementById(PAYMENT_ADAPTER_MOUNT_NODE_ID);
    if (!mountElement) {
      return;
    }

    try {
      if (adapterStatus !== "ready") {
        await preloadAdapter(selectedPaymentMethodCode);
      }

      const { unmount } = await mountAdapter({
        orderId: orderData.number,
        orderEntityId: orderData.orderId,
        paymentMethodCode: selectedPaymentMethodCode,
        isSubscription: checkIfSubscriptionOrder(orderData.items),
        mountElement,
      });

      unmountAdapterRef.current = unmount;
    } catch {
      const failurePath = SinglePageCheckoutRoutes.getOrderFailedPaymentPath(orderData.number);
      navigate(failurePath);
      window.scrollTo(0, 0);
    }
  }, [orderData, selectedPaymentMethodCode, adapterStatus, preloadAdapter, mountAdapter, navigate]);

  const onSubmit = async () => {
    if (!orderData || !selectedPaymentMethodCode) {
      return;
    }

    // Block re-entrant submits while a card session is already mounting/active,
    // so a payment method switch can't race a stale card session.
    if (isPaymentSessionActive || paymentInitiationStatus === "loading") {
      return;
    }

    saveOrderCheckoutFlow(orderData.number, "standard_flow");

    if (isPayOnDelivery) {
      await handlePayOndeliveryOrderSubmit();
      return;
    }

    if (securedFieldsPayment) {
      await handlePaymentProcess({
        orderNumber: orderData.number,
        entityId: orderData.orderId,
        paymentMethodCode: selectedPaymentMethodCode,
        isSubscription: checkIfSubscriptionOrder(orderData.items),
        securedFieldsPayment,
      });
      return;
    }

    if (isOrderPagePaymentFlowEnabled) {
      setIsPaymentAdapterActive(true);
      return;
    }

    await handlePaymentProcess({
      orderNumber: orderData.number,
      entityId: orderData.orderId,
      paymentMethodCode: selectedPaymentMethodCode,
      isSubscription: checkIfSubscriptionOrder(orderData.items),
    });
  };

  const [isPaymentAdapterActive, setIsPaymentAdapterActive] = useState(isPaymentMethodPreselected);
  const hasMountedAdapterRef = useRef(false);
  const unmountAdapterRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if ((!isPaymentMethodPreselected && !isPaymentAdapterActive) || hasMountedAdapterRef.current) {
      return;
    }

    if (!orderData || !selectedPaymentMethodCode || isPayOnDelivery) {
      return;
    }

    hasMountedAdapterRef.current = true;
    navigate(location.pathname, { replace: true, state: {} });

    if (securedFieldsPayment) {
      handlePaymentProcess({
        orderNumber: orderData.number,
        entityId: orderData.orderId,
        paymentMethodCode: selectedPaymentMethodCode,
        isSubscription: checkIfSubscriptionOrder(orderData.items),
        securedFieldsPayment,
      });
      return;
    }

    if (isOrderPagePaymentFlowEnabled) {
      performMount();

      return () => {
        unmountAdapterRef.current?.();
      };
    }

    handlePaymentProcess({
      orderNumber: orderData.number,
      entityId: orderData.orderId,
      paymentMethodCode: selectedPaymentMethodCode,
      isSubscription: checkIfSubscriptionOrder(orderData.items),
    });
  }, [
    isPaymentMethodPreselected,
    isPaymentAdapterActive,
    orderData,
    selectedPaymentMethodCode,
    isPayOnDelivery,
    securedFieldsPayment,
    adapterStatus,
    preloadAdapter,
    mountAdapter,
    navigate,
    location.pathname,
    performMount,
    isOrderPagePaymentFlowEnabled,
    handlePaymentProcess,
  ]);

  const hasPaymentAdapter =
    isOrderPagePaymentFlowEnabled && isPaymentAdapterActive && !isPayOnDelivery && !securedFieldsPayment;

  return (
    <ConsentAndPayFormRenderer
      isRetryingPayment={isRetryingPayment}
      formFieldsConfig={formFieldsConfig}
      defaultValues={formFieldsDefaultValues}
      onSubmit={onSubmit}
      isPayOnDelivery={isPayOnDelivery}
      isConsentEnabled={isConsentEnabled}
      isPaymentMethodPreselected={isPaymentMethodPreselected}
      isOrderPagePaymentFlowEnabled={isOrderPagePaymentFlowEnabled}
      hasPaymentAdapter={hasPaymentAdapter}
    />
  );
};
